import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Lock, Plus, Trash2 } from "lucide-react"
import { type FormEvent, useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Modal } from "@/componentes/ui/Modal"

/**
 * Quién puede qué: la matriz rol × permiso.
 *
 * ⚠ **El grano es la ACCIÓN, no el módulo.** `tarea:crear` y `tarea:asignar` se
 * conceden por separado. El 2.0 concedía módulos enteros —tener el módulo implicaba
 * todas sus operaciones— y por eso allí no había forma de dejar que alguien creara
 * tareas sin dejarle además quitárselas a otro.
 *
 * ⚠ **Se guarda la fila COMPLETA de un rol, no la casilla que se tocó.** Mandar la
 * diferencia obligaría a calcularla aquí, y dos pestañas abiertas sobre el mismo rol
 * acabarían aplicándola contra un estado que ya cambió. Se manda el conjunto entero
 * y el último gana, que al menos es un resultado que se puede explicar.
 *
 * ⚠ **Sin guardado automático al marcar.** Repartir permisos no es un ajuste de
 * preferencias: se marca, se mira la fila entera y se guarda. Con guardado al vuelo,
 * una casilla tocada por error ya está aplicada antes de darse cuenta.
 *
 * ⚠ **El servidor puede negarse, y es lo correcto.** Quitarle `usuario:administrar`
 * al último rol que lo tiene cerraría esta misma pantalla para todo el mundo: el
 * servidor responde 409 y aquí se enseña tal cual. No se reimplementa la regla.
 */

type Rol = {
  id: number
  nombre: string
  descripcion: string
  es_sistema: boolean
  usuarios: number
  permisos: string[]
}

type Permiso = {
  id: number
  clave: string
  descripcion: string
  /** El módulo, con su nombre: «Plantillas oficiales», no `plantilla`. */
  grupo?: string
  /** Las entradas del menú que este permiso abre. */
  modulos?: string[]
}

/**
 * Bajo qué título sale un permiso: el nombre del módulo que manda el servidor.
 *
 * ⚠ **El nombre, no el prefijo de la clave.** Se agrupaba por `tarea`, `rotulo`,
 * `persona`… y el título era ese prefijo en crudo: «Plantillas oficiales» no aparecía
 * por ninguna parte —colgaba de `rotulo:ver`— y quien reparte accesos no tenía forma
 * de encontrarla. El prefijo queda de respaldo, y `test_catalogo_accesos.py` impide
 * que un permiso llegue sin grupo.
 */
function grupoDe(permiso: Permiso): string {
  if (permiso.grupo) return permiso.grupo
  const corte = permiso.clave.indexOf(":")
  return corte === -1 ? permiso.clave : permiso.clave.slice(0, corte)
}

export function MatrizPermisos() {
  const clienteQuery = useQueryClient()
  const [rolId, setRolId] = useState<number | null>(null)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [creando, setCreando] = useState(false)
  const [nombre, setNombre] = useState("")
  const [descripcion, setDescripcion] = useState("")

  const roles = useQuery({
    queryKey: ["accesos", "roles"],
    queryFn: () => api.get<Rol[]>("/api/v1/accesos/roles"),
  })
  const permisos = useQuery({
    queryKey: ["permisos"],
    queryFn: () => api.get<Permiso[]>("/api/v1/permisos"),
  })

  const rol = roles.data?.find((r) => r.id === rolId) ?? roles.data?.[0]

  // Al cambiar de rol se recarga la fila desde el servidor. Sin esto, las casillas
  // del rol anterior se quedarían puestas sobre el rol nuevo: lo que se vería sería
  // el reparto de otro, y lo que se guardaría también.
  useEffect(() => {
    if (rol !== undefined) setMarcados(new Set(rol.permisos))
  }, [rol])

  const guardar = useMutation({
    mutationFn: (id: number) =>
      api.put(`/api/v1/accesos/roles/${id}/permisos`, { permisos: [...marcados] }),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["accesos", "roles"] }),
  })

  const crear = useMutation({
    mutationFn: () => api.post<Rol>("/api/v1/accesos/roles", { nombre, descripcion }),
    onSuccess: (creado) => {
      clienteQuery.invalidateQueries({ queryKey: ["accesos", "roles"] })
      setCreando(false)
      setNombre("")
      setDescripcion("")
      setRolId(creado.id)
    },
  })

  const borrar = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/accesos/roles/${id}`),
    onSuccess: () => {
      setRolId(null)
      clienteQuery.invalidateQueries({ queryKey: ["accesos", "roles"] })
    },
  })

  if (roles.isPending || permisos.isPending) {
    return (
      <p role="status" className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando…
      </p>
    )
  }

  if (roles.isError || permisos.isError || rol === undefined) {
    return (
      <p role="alert" className="py-12 text-center text-sm" style={{ color: "var(--destructive)" }}>
        {roles.error instanceof ErrorApi ? roles.error.message : "No se pudieron cargar los roles"}
      </p>
    )
  }

  // Agrupar es lo que hace legible una lista de cincuenta casillas: sin esto es una
  // columna que hay que recorrer entera. El orden de los grupos lo manda el servidor.
  const porModulo = new Map<string, Permiso[]>()
  for (const p of permisos.data ?? []) {
    const grupo = porModulo.get(grupoDe(p))
    if (grupo === undefined) porModulo.set(grupoDe(p), [p])
    else grupo.push(p)
  }

  const alternar = (clave: string) => {
    const siguiente = new Set(marcados)
    if (siguiente.has(clave)) siguiente.delete(clave)
    else siguiente.add(clave)
    setMarcados(siguiente)
  }

  const sucio = rol.permisos.length !== marcados.size || rol.permisos.some((p) => !marcados.has(p))
  const error = guardar.error ?? borrar.error

  function enviarNuevo(evento: FormEvent) {
    evento.preventDefault()
    crear.mutate()
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
      {/* Los roles a la izquierda, sus permisos a la derecha. Una tabla de 5 × 39
          casillas no cabe sin desplazamiento horizontal, y con él se pierde de vista
          de qué rol es la fila que se está marcando. */}
      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="rotulo flex-1">Roles</h2>
          <button
            type="button"
            onClick={() => setCreando(true)}
            aria-haspopup="dialog"
            className="boton-secundario h-8 px-2 text-xs"
          >
            <Plus size={14} aria-hidden={true} />
            Nuevo
          </button>
        </div>
        <ul className="superficie overflow-hidden">
          {roles.data?.map((r, i) => (
            <li
              key={r.id}
              style={i === 0 ? undefined : { borderTop: "1px solid var(--cristal-filo)" }}
            >
              <button
                type="button"
                onClick={() => setRolId(r.id)}
                data-testid={`rol-${r.nombre}`}
                aria-current={r.id === rol.id}
                className="block w-full px-3 py-2 text-left text-sm toque-cristal"
                style={
                  r.id === rol.id
                    ? { backgroundColor: "var(--primary-suave)", color: "var(--primary-texto)" }
                    : undefined
                }
              >
                <span className="flex items-center gap-1.5 font-medium">
                  {r.nombre}
                  {/* Un rol del sistema no se borra: el código reparte permisos por
                      su nombre, y borrarlo desharía esa correspondencia. */}
                  {r.es_sistema && <Lock size={12} aria-label="del sistema" />}
                </span>
                <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                  {r.usuarios} usuario{r.usuarios === 1 ? "" : "s"} · {r.permisos.length} permisos
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <h2 className="rotulo flex-1">Permisos de «{rol.nombre}»</h2>
          {!rol.es_sistema && rol.usuarios === 0 && (
            <button
              type="button"
              onClick={() => borrar.mutate(rol.id)}
              className="boton-secundario h-8 px-2 text-xs"
              style={{ color: "var(--destructive)" }}
            >
              <Trash2 size={14} aria-hidden={true} />
              Borrar rol
            </button>
          )}
          <button
            type="button"
            disabled={!sucio || guardar.isPending}
            onClick={() => guardar.mutate(rol.id)}
            className="boton-primario h-8 px-3 text-xs"
          >
            {guardar.isPending ? "Guardando…" : "Guardar permisos"}
          </button>
        </div>

        {error && (
          <p
            role="alert"
            className="mb-3 flex items-start gap-2 text-sm"
            style={{ color: "var(--destructive)" }}
          >
            <AlertCircle size={16} aria-hidden={true} />
            {error instanceof ErrorApi ? error.message : "No se pudo guardar"}
          </p>
        )}

        <div className="superficie divide-y p-0" style={{ borderColor: "var(--border)" }}>
          {[...porModulo].map(([modulo, lista]) => (
            <fieldset key={modulo} className="p-3" style={{ borderColor: "var(--border)" }}>
              <legend className="sr-only">{modulo}</legend>
              <p
                className="mb-2 text-xs font-semibold tracking-wide uppercase"
                style={{ color: "var(--fg-muted)" }}
              >
                {modulo}
              </p>
              <div className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
                {lista.map((p) => (
                  <label key={p.clave} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={marcados.has(p.clave)}
                      onChange={() => alternar(p.clave)}
                      data-testid={`permiso-${p.clave}`}
                    />
                    <span>
                      <span className="block">{p.descripcion}</span>
                      <span
                        className="block font-mono text-[11px]"
                        style={{ color: "var(--fg-muted)" }}
                      >
                        {p.clave}
                      </span>
                      {/* Qué entradas del menú abre. Es lo que deja ver, por ejemplo,
                          que Carnets cuelga de «Ver el expediente de personas». */}
                      {p.modulos !== undefined && p.modulos.length > 0 && (
                        <span className="block text-[11px]" style={{ color: "var(--fg-muted)" }}>
                          Abre: {p.modulos.join(" · ")}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      </div>

      <Modal
        abierto={creando}
        titulo="Nuevo rol"
        descripcion="Nace sin permisos: se reparten después, mirando la lista entera."
        onCerrar={() => setCreando(false)}
      >
        <form onSubmit={enviarNuevo} aria-label="Nuevo rol">
          <label htmlFor="rol-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="rol-nombre"
            required
            minLength={2}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="campo mb-3"
          />
          <label htmlFor="rol-desc" className="mb-1 block text-xs font-medium uppercase">
            Para qué es
          </label>
          <input
            id="rol-desc"
            required
            minLength={3}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="campo"
          />
          {crear.error && (
            <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
              {crear.error instanceof ErrorApi ? crear.error.message : "No se pudo crear"}
            </p>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCreando(false)}
              className="boton-secundario h-9 px-3 text-sm"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={crear.isPending}
              className="boton-primario h-9 px-3 text-sm"
            >
              Crear rol
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
